import { useState, useCallback } from 'react';
import { apiService } from '../services/api';
import type { CategoryListResponse } from '../types/api-responses';
import { getApiErrorMessage } from '../types/api-errors';

export const useCategories = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const getCategories = useCallback(async (): Promise<CategoryListResponse> => {
        setLoading(true);
        setError(null);
        try {
            return await apiService.getCategories();
        } catch (err: unknown) {
            const msg = getApiErrorMessage(err, 'Failed to fetch categories');
            setError(msg);
            throw new Error(msg);
        } finally {
            setLoading(false);
        }
    }, []);

    const getBusinessCategories = useCallback(async (): Promise<CategoryListResponse> => {
        setLoading(true);
        setError(null);
        try {
            return await apiService.getBusinessCategories();
        } catch (err: unknown) {
            const msg = getApiErrorMessage(err, 'Failed to fetch business categories');
            setError(msg);
            throw new Error(msg);
        } finally {
            setLoading(false);
        }
    }, []);

    const removeCategoryFromBusiness = useCallback(async (categoryId: number): Promise<boolean> => {
        setLoading(true);
        setError(null);
        try {
            return await apiService.removeCategoryFromBusiness(categoryId);
        } catch (err: unknown) {
            const msg = getApiErrorMessage(err, 'Failed to remove category');
            setError(msg);
            throw new Error(msg);
        } finally {
            setLoading(false);
        }
    }, []);

    /**
     * CR-2026-05-15 — Subscribe the current business to an existing master
     * category. Powers the My Business → Categories "+ Browse" UX. Idempotent
     * server-side; safe to call without pre-checking the BusinessCategory link.
     */
    const subscribeBusinessCategory = useCallback(async (categoryId: number): Promise<boolean> => {
        setLoading(true);
        setError(null);
        try {
            return await apiService.subscribeBusinessCategory(categoryId);
        } catch (err: unknown) {
            const msg = getApiErrorMessage(err, 'Failed to subscribe to category');
            setError(msg);
            throw new Error(msg);
        } finally {
            setLoading(false);
        }
    }, []);

    return {
        getCategories,
        getBusinessCategories,
        removeCategoryFromBusiness,
        subscribeBusinessCategory,
        loading,
        error
    };
};
